import { useCallback, useState } from 'react';
import { MasterUpload, MasterUploadOutcome } from './useMasterUpload';

interface UseMasterPickProps {
  upload: MasterUpload;
  /** Runs after the master is saved. */
  onSaved?: () => void;
}

/**
 * What happens when the user picks a master in a place that uploads at once (designs f1, f2, k3).
 *
 * With the rights already confirmed, the pick uploads. Without them, the file waits on the client
 * (`pending`) until the user ticks the box and presses Upload (design g1). A 400
 * `M24_MASTER_RIGHTS_REQUIRED` answer puts the file back in that state.
 */
export function useMasterPick({ upload, onSaved }: UseMasterPickProps) {
  const [pending, setPending] = useState<File>();
  const [rightsChecked, setRightsChecked] = useState(false);
  const [uploadingName, setUploadingName] = useState<string>();
  const { upload: send, needsRights } = upload;

  const sendFile = useCallback(
    async (file: File, rightsConfirmed: boolean): Promise<MasterUploadOutcome> => {
      setUploadingName(file.name);
      const outcome = await send(file, rightsConfirmed);
      setUploadingName(undefined);
      if (outcome === 'saved') {
        setPending(undefined);
        setRightsChecked(false);
        onSaved?.();
      } else if (outcome === 'rightsRequired') {
        setPending(file);
      }
      return outcome;
    },
    [onSaved, send]
  );

  const pick = useCallback(
    (file: File) => {
      if (needsRights) {
        setPending(file);
        return;
      }
      sendFile(file, false).catch(console.error);
    },
    [needsRights, sendFile]
  );

  const confirm = useCallback(() => {
    if (pending !== undefined && rightsChecked) {
      sendFile(pending, true).catch(console.error);
    }
  }, [pending, rightsChecked, sendFile]);

  const cancel = useCallback(() => {
    setPending(undefined);
    setRightsChecked(false);
  }, []);

  return {
    pending,
    rightsChecked,
    setRightsChecked,
    pick,
    confirm,
    cancel,
    uploadingName,
    isUploading: upload.progress !== null,
  };
}
